import type {
  ThreadV4LayoutJson,
  ThreadV4ViewSpec,
  ThreadV4WidgetDock,
  ThreadV4WidgetKind,
  ThreadV4WidgetSpec,
} from "../../api/threadV4Views";

export const THREAD_V4_WIDGET_KINDS: readonly ThreadV4WidgetKind[] = [
  "ask-chart",
  "asset-rail",
  "build-surface",
  "coverage-map",
  "dag",
  "flowfield",
  "geometry",
  "markdown-note",
  "hq-margins",
  "mesh",
  "notebook",
  "plot",
  "plot-recall",
  "table",
];

export function isUuidLike(value: string | null | undefined): value is string {
  return Boolean(value?.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i));
}

export function makeThreadV4Widget(
  id: string,
  kind: ThreadV4WidgetKind,
  title: string,
  x: number,
  y: number,
  w: number,
  h: number,
  dock: ThreadV4WidgetDock = "canvas",
  bindings: Record<string, unknown> = {},
): ThreadV4WidgetSpec {
  return { id, kind, title, position: { x, y, w, h, dock }, bindings };
}

const widget = makeThreadV4Widget;

function view(
  projectId: string,
  branch: string,
  id: string,
  name: string,
  slug: string,
  widgets: ThreadV4WidgetSpec[],
): ThreadV4ViewSpec {
  return {
    id,
    project_id: projectId,
    branch,
    name,
    slug,
    category: "standard",
    visibility: "project",
    owner_id: "thread-system",
    layout_json: {
      schema_version: "thread.view.layout.v1",
      mode: "dashboard",
      cols: 12,
      row_height: 128,
      widgets,
    },
    widget_state_json: {},
    binding_json: {},
    metadata: { standard_view: slug },
    thumbnail_asset_id: null,
    is_default: slug === "default",
    created_at: null,
    updated_at: null,
  };
}

export function makeStandardThreadV4Views(projectId: string, branch = "main"): ThreadV4ViewSpec[] {
  return [
    view(projectId, branch, "standard-default", "Default", "default", [
      widget("geometry", "geometry", "Geometry", 0, 0, 2, 2, "left"),
      widget("table", "table", "Whole table", 2, 0, 7, 4),
      widget("asset-rail", "asset-rail", "Artifacts", 9, 0, 3, 3, "right"),
      widget("flowfield", "flowfield", "Flowfield", 9, 3, 3, 2, "right"),
      widget("dag", "dag", "DAG minimap", 0, 2, 2, 2, "left"),
      widget("notebook", "notebook", "Spool notebook", 0, 4, 12, 2, "bottom", { scope: "project", sources: ["manual", "agent", "slack", "gmail", "drive", "flow360", "github"] }),
    ]),
  ];
}

export function makeCustomDashboardTemplate(
  projectId: string,
  branch = "main",
  suffix = "draft",
): ThreadV4ViewSpec {
  const id = `local-custom-${suffix}`;
  return {
    id,
    project_id: projectId,
    branch,
    name: "Custom dashboard",
    slug: `custom-dashboard-${suffix}`,
    category: "custom",
    visibility: "project",
    owner_id: "local-user",
    layout_json: {
      schema_version: "thread.view.layout.v1",
      mode: "dashboard",
      cols: 12,
      row_height: 128,
      widgets: [
        widget("geometry", "geometry", "Geometry", 0, 0, 3, 2, "left"),
        widget("mesh", "mesh", "Mesh", 0, 2, 3, 3, "left"),
        widget("dag", "dag", "DAG", 3, 0, 3, 2),
        widget("table", "table", "Whole table", 3, 2, 4, 3),
        widget("flowfield", "flowfield", "Flowfield", 7, 0, 5, 3, "right"),
        widget("plot", "plot", "Plot", 7, 3, 5, 2, "bottom"),
        widget("notebook", "notebook", "Spool notebook", 0, 5, 12, 2, "bottom", { scope: "project", sources: ["manual", "agent", "slack", "gmail", "drive", "flow360", "github"] }),
      ],
    },
    widget_state_json: {},
    binding_json: {},
    metadata: { source: "local-template" },
    thumbnail_asset_id: null,
    is_default: false,
    created_at: null,
    updated_at: null,
  };
}

export function validateThreadV4Layout(layout: ThreadV4LayoutJson): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  if (!Array.isArray(layout.widgets)) errors.push("widgets must be an array");
  for (const widget of layout.widgets ?? []) {
    if (ids.has(widget.id)) errors.push(`duplicate widget id ${widget.id}`);
    ids.add(widget.id);
    if (!THREAD_V4_WIDGET_KINDS.includes(widget.kind)) errors.push(`unsupported widget kind ${widget.kind}`);
    const { x, y, w, h } = widget.position;
    if (![x, y, w, h].every(Number.isInteger)) errors.push(`widget ${widget.id} position must use integers`);
    if (w <= 0 || h <= 0) errors.push(`widget ${widget.id} must have positive size`);
  }
  return errors;
}

export function dockThreadV4Widget(
  layout: ThreadV4LayoutJson,
  widgetId: string,
  dock: ThreadV4WidgetDock,
): ThreadV4LayoutJson {
  const cols = layout.cols || 12;
  return {
    ...layout,
    widgets: layout.widgets.map((item) => {
      if (item.id !== widgetId) return item;
      const position =
        dock === "left" ? { x: 0, y: item.position.y, w: Math.min(3, cols), h: item.position.h, dock } :
        dock === "right" ? { x: Math.max(0, cols - 4), y: item.position.y, w: Math.min(4, cols), h: item.position.h, dock } :
        dock === "bottom" ? { x: item.position.x, y: 5, w: Math.min(item.position.w, cols), h: item.position.h, dock } :
        dock === "floating" ? { x: Math.max(0, cols - 5), y: 1, w: Math.min(5, cols), h: item.position.h, dock } :
        { ...item.position, dock };
      return { ...item, position };
    }),
  };
}

export function autoArrangeThreadV4Widgets(layout: ThreadV4LayoutJson): ThreadV4LayoutJson {
  const cols = layout.cols || 12;
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  const widgets = layout.widgets.map((item) => {
    const w = Math.min(Math.max(item.position.w, 3), cols);
    const h = Math.max(item.position.h, 2);
    if (x + w > cols) {
      x = 0;
      y += rowHeight;
      rowHeight = 0;
    }
    const next = { ...item, position: { ...item.position, x, y, w, h, dock: item.position.dock ?? "canvas" } };
    x += w;
    rowHeight = Math.max(rowHeight, h);
    return next;
  });
  return { ...layout, widgets };
}
